import { useState, useEffect, useCallback, useRef } from 'react';
import { BrandInfo, BrandVoiceGuide, ContentMix, DEFAULT_CONTENT_MIX, Workspace } from '@agency-smm/shared';
import { apiClient } from '../../services/apiClient';
import { flattenJsonForEdit } from '../../utils/formatBrandData';

// ─── Tipos del formulario unificado ──────────────────────────────────────────
// formData incluye TODOS los campos de brand_info más el nombre del workspace
// (que se persiste por separado al workspace) y el sourceUrl.

/** Secciones que pueden auto-completarse con la IA. */
export type AISection = 'identity' | 'personality' | 'voice' | 'content' | 'visual';

/** Llaves de tags top-level (arrays de string en brand_info). */
export type BrandTagField = 'keywords' | 'competitors' | 'productsServices' | 'postTopics';

/** Llaves de tags dentro del voiceGuide. */
export type VoiceTagField = 'wordsToUse' | 'wordsToAvoid';

/**
 * Forma del formulario. `Partial<BrandInfo>` cubre todos los campos de marca;
 * agregamos `workspaceName` que vive en el workspace, no en brand_info.
 */
export type BrandFormData = Partial<BrandInfo> & {
  /** Nombre del workspace (editable en el header; se guarda al workspace). */
  workspaceName: string;
};

// voiceGuide vacío reutilizable (load + reset).
const EMPTY_VOICE_GUIDE: BrandVoiceGuide = {
  purpose: '',
  audience: '',
  tone: '',
  emotionalTone: '',
  character: '',
  syntax: '',
  language: '',
  wordsToUse: [],
  wordsToAvoid: [],
};

// Estado inicial del formulario antes de cargar datos remotos.
const INITIAL_FORM: BrandFormData = {
  workspaceName: '',
  description: '',
  targetAudience: '',
  tone: '',
  keywords: [],
  sourceUrl: '',
  businessOverview: '',
  coreIdentity: '',
  marketPositioning: '',
  competitors: [],
  customerProfile: '',
  emotionalBenefits: '',
  brandStory: '',
  brandPersonality: '',
  productsServices: [],
  serviceAreas: '',
  phoneNumber: '',
  address: '',
  businessHours: '',
  whatsapp: '',
  email: '',
  postTopics: [],
  voiceGuide: { ...EMPTY_VOICE_GUIDE },
  contentMix: { ...DEFAULT_CONTENT_MIX },
  brandColors: [],
  brandFonts: [],
};

// Friendly labels por sección IA — usados en el modal + toast.
export const SECTION_LABELS: Record<AISection, string> = {
  identity: 'Brand Identity',
  personality: 'Brand Personality',
  voice: 'Brand Voice Guide',
  content: 'Content Strategy',
  visual: 'Brand Visual Identity',
};

// Aplica flattenJsonForEdit a los campos de texto que pueden venir como JSON
// crudo desde el análisis de IA (scrape / doc). Idempotente sobre texto plano.
function flattenBrandTextFields(data: BrandInfo): Partial<BrandInfo> {
  return {
    description: flattenJsonForEdit(data.description),
    targetAudience: flattenJsonForEdit(data.targetAudience),
    tone: flattenJsonForEdit(data.tone),
    businessOverview: flattenJsonForEdit(data.businessOverview),
    coreIdentity: flattenJsonForEdit(data.coreIdentity),
    marketPositioning: flattenJsonForEdit(data.marketPositioning),
    customerProfile: flattenJsonForEdit(data.customerProfile),
    emotionalBenefits: flattenJsonForEdit(data.emotionalBenefits),
    brandStory: flattenJsonForEdit(data.brandStory),
    brandPersonality: flattenJsonForEdit(data.brandPersonality),
  };
}

// Normaliza un BrandInfo remoto al shape del formulario (flatten + defaults).
function brandInfoToForm(data: BrandInfo, workspaceName: string): BrandFormData {
  return {
    ...INITIAL_FORM,
    ...data,
    workspaceName,
    sourceUrl: data.sourceUrl ?? '',
    ...flattenBrandTextFields(data),
    contentMix: data.contentMix || { ...DEFAULT_CONTENT_MIX },
    voiceGuide: data.voiceGuide || { ...EMPTY_VOICE_GUIDE },
  };
}

export interface UseBrandFormResult {
  formData: BrandFormData;
  isLoading: boolean;
  isSaving: boolean;
  isDirty: boolean;
  error: string;
  success: string;
  setError: (v: string) => void;
  setSuccess: (v: string) => void;
  // Setters de campos de formulario.
  setField: <K extends keyof BrandFormData>(field: K, value: BrandFormData[K]) => void;
  setVoiceGuideField: (field: keyof BrandVoiceGuide, value: string) => void;
  setContentMix: (field: keyof ContentMix, value: number) => void;
  addTag: (field: BrandTagField, value: string) => void;
  removeTag: (field: BrandTagField, index: number) => void;
  addVoiceTag: (field: VoiceTagField, value: string) => void;
  removeVoiceTag: (field: VoiceTagField, index: number) => void;
  // Persistencia.
  save: () => Promise<void>;
  reload: () => Promise<void>;
  /** Mergea un BrandInfo recién insertado (scrape / doc) sobre el formData. */
  mergeBrandInfo: (data: BrandInfo) => void;
  // AI fill.
  aiSection: AISection | null;
  aiSources: string[];
  aiBusy: boolean;
  aiError: string;
  openAiFill: (section: AISection) => void;
  closeAiFill: () => void;
  updateAiSource: (i: number, value: string) => void;
  addAiSource: () => void;
  removeAiSource: (i: number) => void;
  runAiFill: () => Promise<void>;
}

/**
 * Hook central del formulario de marca. Una sola fuente de verdad (`formData`)
 * con load (brand-info + workspace), save (PUT brand-info + PUT workspace si
 * cambió el nombre), isDirty global, helpers de tags/voiceGuide/contentMix, y
 * el flujo de AI fill por sección.
 */
export function useBrandForm(workspaceId: string | undefined): UseBrandFormResult {
  const [formData, setFormData] = useState<BrandFormData>(INITIAL_FORM);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Snapshot del último estado persistido. isDirty se DERIVA comparando contra
  // esto (no es un latch de una vía): revertir un cambio vuelve a estado limpio.
  const savedSnapshotRef = useRef<string>('');
  const isDirty = JSON.stringify(formData) !== savedSnapshotRef.current;

  // AI fill state.
  const [aiSection, setAiSection] = useState<AISection | null>(null);
  const [aiSources, setAiSources] = useState<string[]>(['']);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState('');

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setIsLoading(true);
    try {
      // Workspace primero (para el nombre del header). No es fatal si falla.
      let workspaceName = '';
      try {
        const ws = await apiClient.get<Workspace>(`/api/workspaces/${workspaceId}`);
        workspaceName = ws.name || '';
      } catch {
        // strip sin nombre
      }

      const data = await apiClient.get<BrandInfo | null>(`/api/workspaces/${workspaceId}/brand-info`);
      const next = data ? brandInfoToForm(data, workspaceName) : { ...INITIAL_FORM, workspaceName };
      setFormData(next);
      savedSnapshotRef.current = JSON.stringify(next);
    } catch (err) {
      console.error('Failed to load brand form:', err);
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const setField = useCallback<UseBrandFormResult['setField']>((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const setVoiceGuideField = useCallback((field: keyof BrandVoiceGuide, value: string) => {
    setFormData((prev) => ({
      ...prev,
      voiceGuide: { ...(prev.voiceGuide ?? EMPTY_VOICE_GUIDE), [field]: value },
    }));
  }, []);

  const setContentMix = useCallback((field: keyof ContentMix, value: number) => {
    setFormData((prev) => ({
      ...prev,
      contentMix: { ...(prev.contentMix ?? DEFAULT_CONTENT_MIX), [field]: value },
    }));
  }, []);

  const addTag = useCallback((field: BrandTagField, value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setFormData((prev) => {
      const current = (prev[field] as string[] | undefined) || [];
      if (current.includes(trimmed)) return prev;
      return { ...prev, [field]: [...current, trimmed] };
    });
  }, []);

  const removeTag = useCallback((field: BrandTagField, index: number) => {
    setFormData((prev) => {
      const current = (prev[field] as string[] | undefined) || [];
      return { ...prev, [field]: current.filter((_, i) => i !== index) };
    });
  }, []);

  const addVoiceTag = useCallback((field: VoiceTagField, value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setFormData((prev) => {
      const guide = prev.voiceGuide ?? EMPTY_VOICE_GUIDE;
      const current = guide[field] || [];
      if (current.includes(trimmed)) return prev;
      return { ...prev, voiceGuide: { ...guide, [field]: [...current, trimmed] } };
    });
  }, []);

  const removeVoiceTag = useCallback((field: VoiceTagField, index: number) => {
    setFormData((prev) => {
      const guide = prev.voiceGuide ?? EMPTY_VOICE_GUIDE;
      const current = guide[field] || [];
      return { ...prev, voiceGuide: { ...guide, [field]: current.filter((_, i) => i !== index) } };
    });
  }, []);

  const save = useCallback(async () => {
    if (!workspaceId) return;
    setError('');
    setSuccess('');
    setIsSaving(true);
    try {
      const { workspaceName, ...rest } = formData;
      // El nombre del workspace se persiste por separado, solo si cambió.
      if (workspaceName.trim()) {
        await apiClient.put(`/api/workspaces/${workspaceId}`, { name: workspaceName.trim() });
      }
      // Saca llaves no persistibles que arrastran load/merge (el server las ignora,
      // pero no las mandamos como basura en el body).
      const brand: Record<string, unknown> = { ...rest };
      delete brand.id;
      delete brand.createdAt;
      // PUT único de brand-info (sourceUrl incluido).
      await apiClient.put(`/api/workspaces/${workspaceId}/brand-info`, {
        ...brand,
        sourceUrl: (formData.sourceUrl ?? '').trim(),
      });
      savedSnapshotRef.current = JSON.stringify(formData);
      setSuccess('Brand saved just now');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save brand');
    } finally {
      setIsSaving(false);
    }
  }, [workspaceId, formData]);

  // Mergea un BrandInfo recién insertado (scrape / doc). No pisa el nombre del
  // workspace; sí re-flatten los campos de texto. Marca dirty para que el user
  // revise y confirme con Save.
  const mergeBrandInfo = useCallback((data: BrandInfo) => {
    setFormData((prev) => ({
      ...prev,
      ...data,
      workspaceName: prev.workspaceName,
      sourceUrl: data.sourceUrl ?? prev.sourceUrl ?? '',
      ...flattenBrandTextFields(data),
      contentMix: data.contentMix || prev.contentMix || { ...DEFAULT_CONTENT_MIX },
      voiceGuide: data.voiceGuide || prev.voiceGuide || { ...EMPTY_VOICE_GUIDE },
    }));
  }, []);

  // ─── AI fill ────────────────────────────────────────────────────────────────
  const openAiFill = useCallback((section: AISection) => {
    setAiSection(section);
    const url = formData.sourceUrl?.trim();
    setAiSources(url ? [url] : ['']);
    setAiError('');
  }, [formData.sourceUrl]);

  const closeAiFill = useCallback(() => {
    if (!aiBusy) setAiSection(null);
  }, [aiBusy]);

  const updateAiSource = useCallback((i: number, value: string) => {
    setAiSources((prev) => prev.map((s, idx) => (idx === i ? value : s)));
  }, []);

  const addAiSource = useCallback(() => setAiSources((prev) => [...prev, '']), []);

  const removeAiSource = useCallback((i: number) => {
    setAiSources((prev) => (prev.length === 1 ? [''] : prev.filter((_, idx) => idx !== i)));
  }, []);

  const runAiFill = useCallback(async () => {
    if (!aiSection || !workspaceId) return;
    const sources = aiSources.map((s) => s.trim()).filter(Boolean);
    if (sources.length === 0) {
      setAiError('Add at least one URL so the AI can search.');
      return;
    }
    setAiBusy(true);
    setAiError('');
    try {
      type AIFillResponse = { section: string; fields: Record<string, unknown>; fetchedSources: string[] };
      const res = await apiClient.post<AIFillResponse>(
        `/api/workspaces/${workspaceId}/brand-info/ai-fill`,
        { section: aiSection, sources }
      );

      const f = res.fields || {};
      const isStr = (v: unknown): v is string => typeof v === 'string';
      const isStrArr = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === 'string');

      setFormData((prev) => {
        const next: BrandFormData = { ...prev };
        if (aiSection === 'identity') {
          if (isStr(f.businessOverview) && f.businessOverview) next.businessOverview = f.businessOverview;
          if (isStr(f.coreIdentity) && f.coreIdentity) next.coreIdentity = f.coreIdentity;
          if (isStr(f.marketPositioning) && f.marketPositioning) next.marketPositioning = f.marketPositioning;
          if (isStr(f.targetAudience) && f.targetAudience) next.targetAudience = f.targetAudience;
          if (isStr(f.customerProfile) && f.customerProfile) next.customerProfile = f.customerProfile;
          if (isStrArr(f.competitors) && f.competitors.length) next.competitors = f.competitors;
        } else if (aiSection === 'personality') {
          if (isStr(f.brandPersonality) && f.brandPersonality) next.brandPersonality = f.brandPersonality;
          if (isStr(f.brandStory) && f.brandStory) next.brandStory = f.brandStory;
          if (isStr(f.emotionalBenefits) && f.emotionalBenefits) next.emotionalBenefits = f.emotionalBenefits;
          if (isStrArr(f.productsServices) && f.productsServices.length) next.productsServices = f.productsServices;
          if (isStr(f.serviceAreas) && f.serviceAreas) next.serviceAreas = f.serviceAreas;
          if (isStr(f.phoneNumber) && f.phoneNumber) next.phoneNumber = f.phoneNumber;
        } else if (aiSection === 'voice') {
          // El backend devuelve voicePurpose/voiceTone/etc — remapear al voiceGuide.
          next.voiceGuide = {
            ...(next.voiceGuide ?? EMPTY_VOICE_GUIDE),
            ...(isStr(f.voicePurpose) && f.voicePurpose ? { purpose: f.voicePurpose } : {}),
            ...(isStr(f.voiceTone) && f.voiceTone ? { tone: f.voiceTone } : {}),
            ...(isStr(f.emotionalTone) && f.emotionalTone ? { emotionalTone: f.emotionalTone } : {}),
            ...(isStr(f.voiceCharacter) && f.voiceCharacter ? { character: f.voiceCharacter } : {}),
            ...(isStrArr(f.wordsToUse) && f.wordsToUse.length ? { wordsToUse: f.wordsToUse } : {}),
            ...(isStrArr(f.wordsToAvoid) && f.wordsToAvoid.length ? { wordsToAvoid: f.wordsToAvoid } : {}),
          };
        } else if (aiSection === 'content') {
          if (isStrArr(f.postTopics) && f.postTopics.length) next.postTopics = f.postTopics;
          if (isStrArr(f.keywords) && f.keywords.length) next.keywords = f.keywords;
        } else if (aiSection === 'visual') {
          // Mergea sin pisar (dedup case-insensitive, cap 6 colores / 3 fonts).
          if (isStrArr(f.brandColors) && f.brandColors.length) {
            const seen = new Set((next.brandColors || []).map((c) => c.toLowerCase()));
            const merged = [...(next.brandColors || [])];
            for (const c of f.brandColors) {
              if (!seen.has(c.toLowerCase())) { merged.push(c); seen.add(c.toLowerCase()); }
            }
            next.brandColors = merged.slice(0, 6);
          }
          if (isStrArr(f.brandFonts) && f.brandFonts.length) {
            const seen = new Set((next.brandFonts || []).map((c) => c.toLowerCase()));
            const merged = [...(next.brandFonts || [])];
            for (const c of f.brandFonts) {
              if (!seen.has(c.toLowerCase())) { merged.push(c); seen.add(c.toLowerCase()); }
            }
            next.brandFonts = merged.slice(0, 3);
          }
        }
        return next;
      });

      setSuccess(`✨ ${SECTION_LABELS[aiSection]} updated with AI. Review and save.`);
      setTimeout(() => setSuccess(''), 4000);
      setAiSection(null);
    } catch (err) {
      setAiError(err instanceof Error ? err.message : 'AI search failed');
    } finally {
      setAiBusy(false);
    }
  }, [aiSection, aiSources, workspaceId]);

  return {
    formData,
    isLoading,
    isSaving,
    isDirty,
    error,
    success,
    setError,
    setSuccess,
    setField,
    setVoiceGuideField,
    setContentMix,
    addTag,
    removeTag,
    addVoiceTag,
    removeVoiceTag,
    save,
    reload: load,
    mergeBrandInfo,
    aiSection,
    aiSources,
    aiBusy,
    aiError,
    openAiFill,
    closeAiFill,
    updateAiSource,
    addAiSource,
    removeAiSource,
    runAiFill,
  };
}
